import type { Locale } from "date-fns";
import { enUS, pt } from "date-fns/locale";
import { getCurrentLanguage, type Language } from "@/lib/i18n";

// Locale for Intl / toLocale* formatting.
// English keeps whatever locale the call site used before translation (some use
// "en-US", some "en-GB", some the browser default via undefined), so English output
// does not change. Portuguese always formats as pt-PT. The English locale is a required
// argument on purpose: a default would turn an explicit `undefined` into "en-US".
export function intlLocaleFor(
  language: Language,
  englishLocale: string | undefined,
): string | undefined {
  return language === "pt-PT" ? "pt-PT" : englishLocale;
}

/** Non hook version, for code outside components. Uses the active language. */
export function getIntlLocale(
  englishLocale: string | undefined,
): string | undefined {
  return intlLocaleFor(getCurrentLanguage(), englishLocale);
}

// date-fns locale: month and weekday names in the active language. date-fns has no
// separate pt-PT locale; `pt` is European Portuguese (pt-BR is `ptBR`).
export function dateFnsLocaleFor(language: Language): Locale {
  return language === "pt-PT" ? pt : enUS;
}

/** Non hook version, for code outside components. Uses the active language. */
export function getDateFnsLocale(): Locale {
  return dateFnsLocaleFor(getCurrentLanguage());
}

/**
 * Display replacement for `value.toFixed(digits)`: same digits, no thousands grouping,
 * decimal separator of the active language ("12.50" in English, "12,50" in pt-PT).
 * Only for text shown to the user, never for values parsed back (inputs, URLs, keys).
 */
export function formatFixed(value: number, digits: number): string {
  if (getCurrentLanguage() !== "pt-PT") return value.toFixed(digits);
  return value.toFixed(digits).replace(".", ",");
}

// Intl renders USD as "US$" in pt-PT, while English shows "$". The product wants a plain
// "$" for US dollars in Portuguese. Every other currency keeps the default symbol, so
// CAD, AUD and HKD stay distinguishable from USD.
export function currencyDisplayFor(
  locale: string | undefined,
  currency: string,
): "symbol" | "narrowSymbol" {
  return locale === "pt-PT" && currency.toUpperCase() === "USD"
    ? "narrowSymbol"
    : "symbol";
}
